import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Instagram, 
  Play, 
  Trophy, 
  CreditCard,
  ArrowRight,
  CheckCircle,
  MessageCircle,
  Smartphone,
  GamepadIcon
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const InstagramBio = () => {
  const navigate = useNavigate();

  const handlePlayNow = () => {
    const user = localStorage.getItem('fokatmain_user');
    if (user) {
      navigate('/play-games');
    } else {
      navigate('/signup');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-700"></div>
        </div>

        <div className="relative container mx-auto px-4 pt-16 pb-12">
          {/* Welcome Badge */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-pink-500/20 border border-pink-500/50 rounded-full px-6 py-2 mb-6">
              <Instagram className="w-5 h-5 text-pink-400" />
              <span className="text-pink-400 font-semibold text-sm sm:text-base">Welcome from Instagram!</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
              🎮 Play & Win
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400">
                Mobile Phone! 📱
              </span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-gray-300 font-bold mb-6">
              Sunday Special Competition • 9 PM - 10 PM
            </p>

            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50 px-6 py-2 text-base">
              🏆 Grand Prize: Mobile Phone
            </Badge>
          </div>

          {/* How It Works - Instagram Payment Flow */}
          <div className="max-w-3xl mx-auto mb-12">
            <Card className="bg-black/40 backdrop-blur-xl border-pink-500/30">
              <CardContent className="pt-8 pb-8">
                <h2 className="text-2xl sm:text-3xl font-black text-center text-white mb-8">
                  🎯 How to Participate
                </h2>

                <div className="space-y-6">
                  {/* Step 1 - Instagram Payment */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center">
                        <span className="text-white font-black text-xl">1</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Instagram className="w-6 h-6 text-pink-400" />
                        <h3 className="text-xl font-bold text-white">Payment on Instagram</h3>
                      </div>
                      <p className="text-gray-300 text-base mb-3">
                        Send us a DM on Instagram to make payment (₹10 entry fee)
                      </p>
                      <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-4">
                        <p className="text-pink-300 font-semibold text-sm flex items-center gap-2">
                          <MessageCircle className="w-4 h-4" />
                          DM us on @fokatmain for payment details
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Step 2 - Register on Website */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                        <span className="text-white font-black text-xl">2</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Smartphone className="w-6 h-6 text-blue-400" />
                        <h3 className="text-xl font-bold text-white">Register on Website</h3>
                      </div>
                      <p className="text-gray-300 text-base mb-3">
                        Come to fokatmain.com and create your free account
                      </p>
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <p className="text-blue-300 font-semibold text-sm flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Quick signup - just email & password
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Step 3 - Play Game */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center">
                        <span className="text-white font-black text-xl">3</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <GamepadIcon className="w-6 h-6 text-yellow-400" />
                        <h3 className="text-xl font-bold text-white">Play on Sunday 9 PM</h3>
                      </div>
                      <p className="text-gray-300 text-base mb-3">
                        Login on Sunday 9 PM and play the competition game
                      </p>
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                        <p className="text-yellow-300 font-semibold text-sm flex items-center gap-2">
                          <Trophy className="w-4 h-4" />
                          Highest score wins Mobile Phone!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Important Note */}
                <div className="mt-8 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="text-center text-purple-300 font-semibold text-sm">
                    💡 Important: Complete payment on Instagram first, then play on website
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* CTA Buttons */}
          <div className="max-w-2xl mx-auto text-center mb-12">
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-black text-lg px-8 py-6 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                onClick={() => window.open('https://instagram.com/fokatmain', '_blank')}
              >
                <Instagram className="w-6 h-6 mr-2" />
                Pay on Instagram
                <ArrowRight className="w-6 h-6 ml-2" />
              </Button>
              
              <Button 
                size="lg"
                onClick={handlePlayNow}
                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-black text-lg px-8 py-6 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                <Play className="w-6 h-6 mr-2" />
                Register & Play
                <ArrowRight className="w-6 h-6 ml-2" />
              </Button>
            </div>

            <p className="text-gray-400 text-sm">
              Already paid on Instagram? Click "Register & Play" to create account
            </p>
          </div>

          {/* Competition Details */}
          <div className="max-w-2xl mx-auto">
            <Card className="bg-black/40 border-gray-700">
              <CardContent className="py-8">
                <h3 className="text-center text-xl font-bold text-white mb-6">
                  📅 Competition Schedule
                </h3>
                <div className="grid sm:grid-cols-2 gap-4 text-center">
                  <div className="p-4 bg-gray-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm mb-1">Entry Fee</p>
                    <p className="text-white text-2xl font-black">₹10</p>
                    <p className="text-gray-500 text-xs mt-1">Pay on Instagram</p>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm mb-1">Competition Time</p>
                    <p className="text-white text-2xl font-black">9 PM - 10 PM</p>
                    <p className="text-gray-500 text-xs mt-1">Every Sunday</p>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm mb-1">Winner Declared</p>
                    <p className="text-white text-2xl font-black">10 PM</p>
                    <p className="text-gray-500 text-xs mt-1">Same day</p>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm mb-1">Prize</p>
                    <p className="text-white text-2xl font-black">📱</p>
                    <p className="text-gray-500 text-xs mt-1">Mobile Phone</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Footer */}
          <div className="text-center mt-12 text-gray-400 text-sm space-y-2">
            <p>🎮 100% Skill-Based Gaming • Fair & Transparent</p>
            <p>#Fokatmain #SkillGaming #WinPrizes</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstagramBio;
